/** One slot in a numbered pager: a page number or a gap. */
export type PageItem = number | 'gap';

/** Pages to show before the pager starts collapsing into gaps. */
const MAX_SLOTS = 7;

/**
 * Page numbers for a numbered pager, always first and last page, the current
 * page with one neighbour on each side, and `gap` markers in between.
 *
 * Once there are more than 7 pages the result is always 7 slots long, so the
 * pager keeps the same width while the user pages through a list:
 *   page 1 of 15  → 1 2 3 4 5 … 15
 *   page 8 of 15  → 1 … 7 8 9 … 15
 *   page 15 of 15 → 1 … 11 12 13 14 15
 */
export function pageRange(current: number, totalPages: number): PageItem[] {
  if (totalPages <= 0) return [];
  const page = Math.min(Math.max(1, current), totalPages);
  if (totalPages <= MAX_SLOTS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (page <= 4) return [1, 2, 3, 4, 5, 'gap', totalPages];
  if (page >= totalPages - 3) {
    return [1, 'gap', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, 'gap', page - 1, page, page + 1, 'gap', totalPages];
}

/** 1-based index of the first and last item on `page`, clamped to `total`. */
export function pageBounds(page: number, pageSize: number, total: number): { from: number; to: number } {
  if (total <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  return { from: Math.min(from, total), to: Math.min(page * pageSize, total) };
}
